// On a quote item: put it on an invoice that already exists (Stephanie
// 2026-10-16/19: "copy an item from the quote to the invoice if the invoice is
// already created" — the button belongs on the QUOTE, no banner).
//   • An item added to this quote after it was invoiced → "Add to invoice #X"
//     (the invoice re-adds up from the quote).
//   • An item on a quote that isn't invoiced, when another quote in the project
//     is → "Copy to invoice #Y" (copied there, marked "Billed on invoice #Y" here).
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FilePlus2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { invoiceChoicesFor, type InvoiceTargets } from "@/lib/invoice-targets";


export { invoiceChoicesFor, type InvoiceTargets };

export function CopyToInvoiceButton({
  targets,
  line,
  onDone,
}: {
  targets: InvoiceTargets | undefined;
  line: { id: string; serviceType: string; billedOnInvoiceNumber?: number | null };
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const choice = invoiceChoicesFor(targets, line);
  const run = useMutation({
    mutationFn: async (target: { kind: "add" | "copy"; invoiceId: string }) =>
      target.kind === "add"
        ? api<{ total: string }>(`/invoices/${target.invoiceId}/update-from-quote`, { method: "POST" })
        : api<{ total: string }>(`/invoices/${target.invoiceId}/copy-item`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ line_item_id: line.id }),
          }),
    onSuccess: (r) => {
      setOpen(false);
      setMsg({ ok: true, text: `Done — invoice total now ${Number(r.total).toLocaleString(undefined, { style: "currency", currency: "USD" })}.` });
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      onDone();
    },
    onError: (e) => setMsg({ ok: false, text: apiErrorMessage(e, "Couldn't update the invoice.") }),
  });
  if (!choice) return msg ? <span className={`text-xs font-semibold ${msg.ok ? "text-ok" : "text-risk"}`}>{msg.text}</span> : null;
  const btn = "inline-flex items-center gap-1 rounded-md border border-rule bg-white px-2.5 py-1 text-xs font-semibold text-ink hover:bg-cream-100 disabled:opacity-50";
  return (
    <span className="relative inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {choice.kind === "add" ? (
        <button type="button" className={btn} disabled={run.isPending} onClick={() => run.mutate({ kind: "add", invoiceId: choice.invoiceId })}>
          <FilePlus2 className="h-3.5 w-3.5" /> {run.isPending ? "Adding…" : `Add to invoice #${choice.invoiceNumber}`}
        </button>
      ) : choice.options.length === 1 ? (
        <button
          type="button"
          className={btn}
          disabled={run.isPending || choice.waitingOnJake}
          title={choice.waitingOnJake ? "Waiting on a database step from Jake" : undefined}
          onClick={() => run.mutate({ kind: "copy", invoiceId: choice.options[0]!.invoiceId })}
        >
          <FilePlus2 className="h-3.5 w-3.5" /> {run.isPending ? "Copying…" : `Copy to invoice #${choice.options[0]!.invoiceNumber}`}
        </button>
      ) : (
        <>
          <button
            type="button"
            className={btn}
            disabled={run.isPending || choice.waitingOnJake}
            title={choice.waitingOnJake ? "Waiting on a database step from Jake" : undefined}
            onClick={() => setOpen((v) => !v)}
          >
            <FilePlus2 className="h-3.5 w-3.5" /> Copy to invoice…
          </button>
          {open && (
            <span className="absolute right-0 top-full z-20 mt-1 flex min-w-[220px] flex-col rounded-md border border-rule bg-white p-1 shadow-lg">
              {choice.options.map((o) => (
                <button key={o.invoiceId} type="button" className="rounded px-3 py-2 text-left text-sm text-ink hover:bg-cream-100" onClick={() => run.mutate({ kind: "copy", invoiceId: o.invoiceId })}>
                  Invoice #{o.invoiceNumber} <span className="text-muted">(quote #{o.orderNumber})</span>
                </button>
              ))}
            </span>
          )}
        </>
      )}
      {msg && !msg.ok ? <span role="alert" className="text-xs font-semibold text-risk">{msg.text}</span> : null}
    </span>
  );
}
